import { type KeyboardEvent, memo, type MouseEvent, useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import { useEntityDisplayName } from '@/lib/crm/hooks/useEntityDisplayName';
import { cx } from '@/utils/cx';

import {
  displayNameButtonLabel,
  isTriggerButtonRecordOpen,
  OPEN_RECORD_CLASSES,
  type TriggerButtonDescriptor,
  type TriggerButtonTarget,
} from './ActionOverview.helpers';

/**
 * A single "View X" shortcut button. Extracted so its click/keydown handlers
 * are stable `useCallback`s (per the frontend stable-reference rules) and so
 * it can stop event propagation — defensive against the accordion's
 * full-card overlay trigger.
 *
 * For contact/company buttons (`descriptor.displayNameEntityId` set) the label
 * is resolved store-first via `useEntityDisplayName`: it reads the entity's
 * live display name from the CRM store — fetching only the name properties if
 * it isn't cached — and prefers that fresh name over the point-in-time `label`
 * from the trigger fetch, matching the expanded card's store-first behavior
 * (A1) so a rename can't leave the collapsed shortcut showing a stale name.
 * The name-only hook (rather than `useCrmObjectSnapshot`) keeps a shortcut
 * label from triggering a full CRM-object hydration it never reads.
 */
interface TriggerActionButtonProps {
  descriptor: TriggerButtonDescriptor;
  orgId: number | undefined;
  onActivate: (target: TriggerButtonTarget) => void;
}

// Each field guards against a distinct re-render trigger a default shallow-prop
// comparison would get wrong:
// - `descriptor.key` (the trigger's stable entity id, per ActionOverview.helpers.ts)
//   fully determines the button's rendered label/target, so comparing the key
//   instead of object identity avoids a spurious re-render: `deriveTriggerButtons()`
//   recreates the `TriggerButtonDescriptor` array on every trigger-data refetch
//   even when the underlying trigger list is unchanged.
// - `orgId` feeds `useEntityDisplayName` inside the button and can change the
//   resolved display name, so it must still trigger a re-render even though it's
//   not part of `descriptor`.
// - `onActivate` is the click handler; if the parent supplies a new callback the
//   button must re-render so clicks stay wired to current behavior, even though
//   `descriptor.key` is unchanged.
function areTriggerActionButtonPropsEqual(
  prev: TriggerActionButtonProps,
  next: TriggerActionButtonProps
): boolean {
  return (
    prev.descriptor.key === next.descriptor.key &&
    prev.orgId === next.orgId &&
    prev.onActivate === next.onActivate
  );
}

export const TriggerActionButton = memo(function TriggerActionButton({
  descriptor,
  orgId,
  onActivate,
}: TriggerActionButtonProps) {
  // `useEntityDisplayName` is a no-op (no fetch, no name) when the id is
  // undefined, so type-labelled buttons subscribe to nothing.
  const { displayName } = useEntityDisplayName(
    descriptor.displayNameEntityId,
    orgId
  );
  const label = displayName
    ? displayNameButtonLabel(displayName)
    : descriptor.label;

  const { kind } = descriptor.target;
  const targetId =
    descriptor.target.kind === 'entity'
      ? descriptor.target.entityId
      : descriptor.target.url;

  // Highlight the button while its record is open on the slideout stack —
  // including when covered by a further slideout (still open underneath).
  // Read INSIDE this memoized leaf: context updates bypass the props memo,
  // so stack changes re-render only the buttons, not the whole header.
  const slideoutStack = useSlideoutStackContextOptional();
  const isRecordOpen = isTriggerButtonRecordOpen(
    descriptor.target,
    slideoutStack?.stack
  );

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onActivate(descriptor.target);
    },
    // Depend on the primitives (`kind` + `targetId`) that fully determine
    // `descriptor.target` rather than the object itself, which
    // `deriveTriggerButtons()` recreates on every trigger-data refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onActivate, kind, targetId]
  );

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Keep Enter/Space from bubbling to the card's disclosure toggle.
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
    }
  }, []);

  return (
    <Button
      color='secondary'
      size='xs'
      iconLeading={descriptor.icon}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-pressed={isRecordOpen}
      className={cx(isRecordOpen && OPEN_RECORD_CLASSES)}
    >
      {label}
    </Button>
  );
}, areTriggerActionButtonPropsEqual);
